import { Auth0Client } from "@auth0/auth0-spa-js";
import axios from "axios";
import { apiConfig, auth0Config } from "../config";
import { HealthCareServiceInterface } from "../pages/appointment/HealthcareServiceInterface";
import {
  Auth0Role,
  Auth0UserWithDocument,
} from "../pages/facility/types/Auth0User";
import { Organization } from "../types/organization";
import { handleBackwardsCompatibility } from "../utils/organizationUtils";
import querySearchEndpoint from "../utils/querySearchEndpoint";
import { getAuthConfigWithProps, getBasicAuthConfig } from "./authUtils";
import { OrganizationApi } from "./subscription/OrganizationApi";

class ProviderApi {
  private auth0Client: Auth0Client | null = null;

  private organizationApi: OrganizationApi;

  constructor() {
    this.auth0Client = new Auth0Client({
      redirect_uri: window.location.origin,
      ...auth0Config,
    });
    this.organizationApi = new OrganizationApi(this.auth0Client);
  }

  // try {
  //     RestUtils.prepareRefresh(Action.HDA.DESHBOARD.API_UNDER_PROGRESS);
  //     const header = await CommonData.getHeaderWithToken();
  //     let response = await axios({
  //       method: 'GET',
  //       url: Api.healthDiary(id),
  //       headers: header,
  //     });
  //     if (response.data.success === 'OK') {
  //       RestUtils.updateData(Action.HDA.DESHBOARD.API_DATE, response.data);
  //     } else {
  //       RestUtils.restoreData(Action.HDA.DESHBOARD.API_FAIL);
  //     }
  //   } catch (e) {
  //     RestUtils.Console('Error Under getHealthDashboardVital', e);
  //     RestUtils.restoreData(Action.HDA.DESHBOARD.API_FAIL);
  //     Utils.toastError();
  //   }

  async getCurrentPractitioner(providerId: string, cb) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/practitioners/${providerId}`,
        config,
      );
      if (response.data) {
        cb(response.data);
      }
    } catch (e) {
      console.error(`Error when retrieving provider ${JSON.stringify(e)}`);
    }
  }

  async getPractitionerProfile(id) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/practitioners/${id}/profile`,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Error when retrieving provider profile ${JSON.stringify(e)}`,
      );
      throw "An error occurred while loading your profile. Please try again.";
    }
  }

  async getPractitionerProfileById(id) {
    const config = await getBasicAuthConfig(this.auth0Client);

    const response = await axios.get(
      `${apiConfig.providerServiceBase}/practitioners/${id}`,
      config,
    );

    return response.data;
  }

  async findPractitioners(query, signal?: AbortSignal) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );

      const url = new URL(`${apiConfig.providerServiceBase}/practitioners`);
      const response = await querySearchEndpoint(url, query, config);

      if (response) {
        return response;
      }
      console.error({ response });
      throw "An error occurred while searching for practitioners. Please try again.";
    } catch (e) {
      console.error(`Error when retrieving provider ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async updatePractitioner(id: string, formData) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.put(
        `${apiConfig.providerServiceBase}/practitioners/${id}`,
        formData,
        config,
      );

      return response.data;
    } catch (e) {
      console.error(
        `Error updating patient information with ID ${id}. Error: ${JSON.stringify(
          e,
        )}`,
      );
      throw new Error(
        "An error occured while updating the patient's information. Please try again.",
      );
    }
  }

  async getIdTokenClaims() {
    return this.auth0Client.getIdTokenClaims();
  }

  async getUserRoles(orgId: string, userId) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/practitioners/${userId}/member/${orgId}/roles`,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Error when retrieving provider roles ${JSON.stringify(e)}`,
      );
      throw new Error(
        "An error occurred while loading your roles. Please try again.",
      );
    }
  }

  // gets healthcare services provided by a particular organization
  async findHealthcareServices(query, signal?: AbortSignal) {
    try {
      const config = await getAuthConfigWithProps(
        this.auth0Client,
        signal ? { signal } : {},
      );

      const url = new URL(
        `${apiConfig.providerServiceBase}/healthcare-services`,
      );

      const response = await querySearchEndpoint(url, query, config);

      if (response) {
        return response;
      }
      console.error({ response });
      throw "An error occurred while searching for healthcare services. Please try again.";
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async getHealthcareService(id: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/healthcare-services/${id}`,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        "An error occurred while retrieving the healthcare service. Please try again.",
      );
      throw e;
    }
  }

  async saveHealthcareService(healthcareService) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.post(
        `${apiConfig.providerServiceBase}/healthcare-services`,
        healthcareService,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        "An error occurred while saving the healthcare service. Please try again.",
      );
      throw e;
    }
  }

  async updateHealthcareService(
    id: string,
    newData: HealthCareServiceInterface,
  ) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      const response = await axios.put(
        `${apiConfig.providerServiceBase}/healthcare-services/${id}`,
        newData,
        config,
      );
      return response.data;
    } catch (e) {
      console.error(
        "An error occurred while updating the healthcare service. Please try again.",
      );
      throw e;
    }
  }

  async findOrganization(id: string): Promise<Organization> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const url = new URL(
        `${apiConfig.providerServiceBase}/organizations/${id}`,
      );
      const response = await axios.get(url.toString(), config);

      return response.data;
    } catch (e) {
      if (e?.error === "login_required") {
        console.warn("Session expired → logging out");
        await this.auth0Client.logout({ returnTo: window.location.origin });
      } else {
        console.error(e);
        throw e;
      }
    }
  }

  async findOrganizations(query?) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const url = new URL(`${apiConfig.providerServiceBase}/organizations`);
      const response = await querySearchEndpoint(url, query, config);

      if (response) {
        return response;
      }
      console.log({ response });
      throw "An error occurred while searching for organizations. Please try again.";
    } catch (e) {
      console.error(`Error when retrieving organizations ${JSON.stringify(e)}`);
      throw e;
    }
  }

  async updateOrganization(id: string, newData: Organization) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const normalizedData = handleBackwardsCompatibility(newData);

      const response = await axios.put(
        `${apiConfig.providerServiceBase}/organizations/${id}`,
        normalizedData,
        config,
      );
      if (response.data) {
        return response.data;
      }
    } catch (error) {
      // TODO: This error shows to much data
      console.error(
        `Error when updating organization ${JSON.stringify(error)}`,
      );
      throw error;
    }
  }

  async getAuth0Users(
    orgId: string,
    page = 0,
    perPage = 10,
    all = false,
  ): Promise<{ members: Auth0UserWithDocument[]; total: number }> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      if (orgId) {
        config.headers["X-Organization-AuthId"] = orgId;
      }

      const limit = all ? 0 : perPage;
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/auth/members?page=${page}&per_page=${limit}`,
        config,
      );

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async getGlobalUserRoles(): Promise<Auth0Role[]> {
    const config = await getBasicAuthConfig(this.auth0Client);
    const response = await axios.get(
      `${apiConfig.providerServiceBase}/auth/user-roles`,
      config,
    );
    return response.data || [];
  }

  async getAuth0Roles(): Promise<Auth0Role[]> {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);

      const response = await axios.get(
        `${apiConfig.providerServiceBase}/auth/roles`,
        config,
      );

      const excludedRoles = [
        "product catalog manager",
        "super administrator",
        "medial support",
      ];
      return (response.data || []).filter((role: Auth0Role) => {
        if (!role.name) return false;
        const normalized = role.name.toLowerCase().replace(/_/g, " ").trim();
        return !excludedRoles.includes(normalized);
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async addAuth0RolesToUser(
    memberId: string,
    organizationId: string,
    roles: string[],
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    if (organizationId) {
      config.headers["X-Organization-AuthId"] = organizationId;
    }

    const response = await axios.post(
      `${apiConfig.providerServiceBase}/auth/members/${memberId}/organizations/${organizationId}/roles`,
      { roles },
      config,
    );

    return response.data;
  }

  async removeAuth0RolesFromUser(
    memberId: string,
    organizationId: string,
    roles: string[],
  ) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      if (organizationId) {
        config.headers["X-Organization-AuthId"] = organizationId;
      }

      const response = await axios.delete(
        `${apiConfig.providerServiceBase}/auth/members/${memberId}/organizations/${organizationId}/roles`,
        {
          headers: config.headers,
          data: { roles },
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async createAuth0User(
    orgId: string,
    user: { email: string; password: string },
    roles: string[],
  ): Promise<
    [
      (
        | (Auth0UserWithDocument & { authorizationSuccessful: boolean })
        | undefined
      ),
      number,
    ]
  > {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      if (orgId) {
        config.headers["X-Organization-AuthId"] = orgId;
      }

      const response = await axios.post(
        `${apiConfig.providerServiceBase}/auth/members/add`,
        { ...user, roles },
        config,
      );

      return [response.data, response.status];
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async removeUserFromOrganization(userId: string, organizationId: string) {
    try {
      const config = await getBasicAuthConfig(this.auth0Client);
      if (organizationId) {
        config["headers"]["X-Organization-AuthId"] = organizationId;
      }

      const response = await axios.delete(
        `${apiConfig.providerServiceBase}/auth/members/${userId}/organizations/${organizationId}`,
        config,
      );

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // healthCheck
  async healthCheck() {
    try {
      const response = await axios.get(
        `${apiConfig.providerServiceBase}/health`,
      );
      return response.data;
    } catch (e) {
      console.error(
        `Provider Service health check was unsuccessful${JSON.stringify(e)}`,
      );
      throw e;
    }
  }
}

export const providerApi = new ProviderApi();
